import React, { useMemo } from 'react';
import { Box, Text, Stack, HoverCard, Group } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { ChartMargins } from '../hooks';
import { useGetLifeEvents } from '../../../../global/entities/lifeEvents/queries/useGetLifeEvents';
import { useGetPrescriptions } from '../../../../global/entities/Prescription/queries/useGetPrescriptions';
import { Prescription, LifeEvent } from '../types';
import { t } from 'i18next';
import { PatientStore } from '../../../../global/Store/PatientStore';
import { inject } from 'mobx-react';
import { observer } from 'mobx-react';

interface ComboboxItem {
  value: string;
  label: string;
}

interface EventData {
  type: 'prescription' | 'lifeEvent';
  data: any;
}

function isLifeEvent(data: Prescription | LifeEvent): data is LifeEvent {
  return 'title' in data;
}

function toDateString(date: Date | string) {
  return (typeof date === 'string' ? new Date(date) : date)
    .toISOString()
    .split('T')[0];
}

interface CombinedTimelineProps {
  allDates: string[];
  chartMargins: ChartMargins;
  getDatePosition: (date: string) => number;
  contentLeftOffset: number;
  contentWidth: string;
  patientStore?: PatientStore;
}

// Define consistent colors that don't conflict with the line graph
const TIMELINE_COLORS = {
  lifeEvent: '#339af0', // Blue
};

// Per-drug palette for prescription pills — calm clinical hues.
// `base` is used for the fill tint, border and dot; `text` is a darker
// shade that stays legible on the light tint.
const PRESCRIPTION_PALETTE = [
  { base: '#2f6fed', text: '#1d4ed8' },
  { base: '#0d9488', text: '#0f766e' },
  { base: '#d97706', text: '#b45309' },
  { base: '#9333ea', text: '#7e22ce' },
  { base: '#dc2626', text: '#b91c1c' },
  { base: '#0284c7', text: '#0369a1' },
  { base: '#db2777', text: '#be185d' },
  { base: '#65a30d', text: '#4d7c0f' },
];

// Ongoing scripts (no end date, or end date past the chart) fade out
// at the right edge to signal they continue beyond the graph.
const ONGOING_FADE_MASK = 'linear-gradient(90deg, #000 88%, transparent)';

export const CombinedTimeline: React.FC<CombinedTimelineProps> = inject(
  'patientStore',
)(
  observer(
    ({
      allDates,
      chartMargins,
      getDatePosition,
      contentLeftOffset,
      contentWidth,
      patientStore,
    }) => {
      const isMobile = useMediaQuery('(max-width: 768px)');
      const { data: lifeEvents = [] } = useGetLifeEvents(
        patientStore?.patientId,
      );
      const { data: prescriptionData } = useGetPrescriptions(
        patientStore?.patientId,
      );

      const eventsByDate = useMemo(() => {
        const eventsMap: Record<string, EventData[]> = {};

        // Add prescriptions
        prescriptionData?.prescriptions.forEach((prescription) => {
          if (prescription.startDate) {
            const dateKey = toDateString(prescription.startDate);

            if (!eventsMap[dateKey]) eventsMap[dateKey] = [];

            eventsMap[dateKey].push({
              type: 'prescription',
              data: prescription,
            });
          }
        });

        // Add life events
        lifeEvents.forEach((event) => {
          const dateKey = toDateString(event.date);
          // Only add if the date exists in allDates
          if (allDates.includes(dateKey)) {
            if (!eventsMap[dateKey]) eventsMap[dateKey] = [];

            eventsMap[dateKey].push({ type: 'lifeEvent', data: event });
          }
        });

        return eventsMap;
      }, [prescriptionData, lifeEvents, allDates]);

      // Thin out gridlines as the range grows so a 6-month / 1-year view
      // doesn't render hundreds of dashed lines behind the timeline.
      const gridLineIndices = useMemo(() => {
        const total = allDates.length;
        if (total === 0) return [];

        let stride = 1;
        if (total > 186) stride = 30;
        else if (total > 92) stride = 14;
        else if (total > 31) stride = 7;

        const indices: number[] = [];
        for (let i = 0; i < total; i += stride) indices.push(i);
        const lastIndex = total - 1;
        if (indices[indices.length - 1] !== lastIndex) indices.push(lastIndex);
        return indices;
      }, [allDates]);

      // Sort prescriptions by start date
      const sortedPrescriptions = [
        ...(prescriptionData?.prescriptions || []),
      ].sort((a, b) => {
        const aDate = a.startDate ? toDateString(a.startDate) : '';
        const bDate = b.startDate ? toDateString(b.startDate) : '';
        return allDates.indexOf(aDate) - allDates.indexOf(bDate);
      });

      // Calculate row assignments for prescriptions to optimize space
      const rowAssignments: Record<string, number> = {};
      // For each row, keep an array of [startIndex, endIndex] for prescriptions in that row
      const rowRanges: Array<Array<{ start: number; end: number }>> = [];
      sortedPrescriptions.forEach((prescription) => {
        const startIndex = prescription.startDate
          ? allDates.indexOf(toDateString(prescription.startDate))
          : 0;
        let endIndex = prescription.endDate
          ? allDates.indexOf(toDateString(prescription.endDate))
          : allDates.length - 1;

        // if endIndex is -1 , set it to the last index of allDates
        if (endIndex === -1) endIndex = allDates.length - 1;

        let assignedRow = 0;
        let foundRow = false;
        // Try to find a row where this prescription does not overlap
        for (let i = 0; i < rowRanges.length; i++) {
          const ranges = rowRanges[i];
          // If this is a single-day prescription, check if any other prescription in this row has the same start and end index
          if (startIndex === endIndex) {
            const sameDayConflict = ranges.some(
              (r) => r.start === startIndex && r.end === endIndex,
            );
            if (!sameDayConflict) {
              assignedRow = i;
              foundRow = true;
              break;
            }
          } else {
            // For multi-day, check for overlap
            const overlap = ranges.some(
              (r) => !(endIndex < r.start || startIndex > r.end),
            );
            if (!overlap) {
              assignedRow = i;
              foundRow = true;
              break;
            }
          }
        }
        if (!foundRow) {
          assignedRow = rowRanges.length;
          rowRanges.push([]);
        }
        rowRanges[assignedRow].push({ start: startIndex, end: endIndex });
        rowAssignments[prescription.id] = assignedRow;
      });

      // Assign a stable color to each prescription based on sorted order
      const colorByPrescriptionId: Record<
        number,
        (typeof PRESCRIPTION_PALETTE)[number]
      > = {};
      sortedPrescriptions.forEach((prescription, index) => {
        colorByPrescriptionId[prescription.id] =
          PRESCRIPTION_PALETTE[index % PRESCRIPTION_PALETTE.length];
      });

      const lastDate = allDates[allDates.length - 1] ?? '';

      // Calculate the total height needed for prescriptions based on row assignments
      const maxPrescriptionRow = Object.values(rowAssignments).reduce(
        (max, row) => Math.max(max, row),
        0,
      );
      const prescriptionRowHeight = isMobile ? 18 : 22;
      const rowGap = isMobile ? 4 : 6;
      const prescriptionsHeight =
        (maxPrescriptionRow + 1) * (prescriptionRowHeight + rowGap) + 10;

      // Life events will be positioned at the top section
      const lifeEventsHeight = isMobile ? 30 : 40; // Fixed height for life events section
      const timelineHeight = prescriptionsHeight + lifeEventsHeight + 10; // Additional padding

      // Adjust margins for mobile, accounting for YAxis width
      const yAxisWidth = isMobile ? 30 : 40; // Same as YAxis width in line chart
      const adjustedMargins = {
        left:
          (isMobile ? Math.max(30, chartMargins.left) : chartMargins.left) +
          yAxisWidth,
        right: isMobile ? Math.max(10, chartMargins.right) : chartMargins.right,
        top: chartMargins.top,
        bottom: chartMargins.bottom,
      };

      return (
        <Box mt="xs" style={{ width: '100%' }}>
          <Box
            style={{
              position: 'relative',
              height: `${timelineHeight}px`,
              border: '1px solid #eee',
              borderRadius: '4px',
              marginLeft: contentLeftOffset,
              width: contentWidth,
            }}
          >
            {/* Date gridlines */}
            {gridLineIndices.map((index) => {
              const position = (index / (allDates.length - 1)) * 100;
              return (
                <Box
                  key={index}
                  style={{
                    position: 'absolute',
                    left: `${position}%`,
                    transform: 'translateX(-50%)',
                    width: '1px',
                    height: '100%',
                    borderLeft: '1px dashed #ddd',
                  }}
                />
              );
            })}

            {/* Life event markers - grouped by date */}
            {Object.entries(eventsByDate).map(([date, events]) => {
              const lifeEventsForDate = events.filter(
                (event) => event.type === 'lifeEvent',
              );
              if (!lifeEventsForDate.length) return null;

              const position = getDatePosition(date);
              const hasMultiple = lifeEventsForDate.length > 1;

              return (
                <HoverCard
                  key={`event-date-${date}`}
                  position="top"
                  withArrow
                  shadow="md"
                  openDelay={100}
                  closeDelay={200}
                >
                  <HoverCard.Target>
                    <Box
                      style={{
                        position: 'absolute',
                        left: `${position}%`,
                        top: lifeEventsHeight / 2, // Position in the middle of top section
                        transform: 'translate(-50%, -50%)',
                        width: hasMultiple
                          ? isMobile
                            ? '16px'
                            : '18px'
                          : isMobile
                            ? '14px'
                            : '16px',
                        height: hasMultiple
                          ? isMobile
                            ? '16px'
                            : '18px'
                          : isMobile
                            ? '14px'
                            : '16px',
                        borderRadius: '50%',
                        backgroundColor: TIMELINE_COLORS.lifeEvent,
                        cursor: 'pointer',
                        zIndex: 5,
                        // Add a ring around the marker for multiple events
                        boxShadow: hasMultiple
                          ? `0 0 0 3px ${TIMELINE_COLORS.lifeEvent}33`
                          : 'none',
                      }}
                    />
                  </HoverCard.Target>
                  <HoverCard.Dropdown maw={360}>
                    <Stack gap="xs">
                      <Text fw={700}>{date}</Text>
                      {lifeEventsForDate.map((event, index) => (
                        <React.Fragment key={`event-popup-${event.data.id}`}>
                          {index > 0 && (
                            <Box
                              style={{
                                height: '1px',
                                background: '#eee',
                                margin: '4px 0',
                              }}
                            />
                          )}
                          <Box>
                            {isLifeEvent(event.data) && (
                              <>
                                <Text fw={600} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {event.data.title}
                                </Text>
                                <Text size="sm" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {event.data.description}
                                </Text>
                              </>
                            )}
                          </Box>
                        </React.Fragment>
                      ))}
                    </Stack>
                  </HoverCard.Dropdown>
                </HoverCard>
              );
            })}

            {/* Prescription pills - now at the bottom */}
            {Object.values(eventsByDate).flatMap((events) =>
              events
                .filter((event) => event.type === 'prescription')
                .map((prescription) => {
                  const rx = prescription.data;
                  const startPosition = getDatePosition(
                    toDateString(rx.startDate),
                  );
                  const isOngoing =
                    !rx.endDate || toDateString(rx.endDate) > lastDate;
                  const endPosition = isOngoing
                    ? 100
                    : getDatePosition(toDateString(rx.endDate));
                  const width = endPosition - startPosition;
                  const row = rowAssignments[rx.id] || 0;
                  const topPosition =
                    lifeEventsHeight +
                    10 +
                    row * (prescriptionRowHeight + rowGap);
                  const palette =
                    colorByPrescriptionId[rx.id] || PRESCRIPTION_PALETTE[0];
                  // Too narrow to also fit the dose text — drop it (hover keeps it)
                  const tight = width < 12;

                  const unitLabel =
                    prescriptionData?.unitsCombobox.find(
                      (el: ComboboxItem) => Number(el.value) === rx.unitID,
                    )?.label ?? '';
                  const freqLabel =
                    prescriptionData?.doseFrequenciesCombobox.find(
                      (el: ComboboxItem) =>
                        Number(el.value) === rx.doseFrequencyID,
                    )?.label ?? '';
                  const doseLabel = `${rx.dose}${unitLabel} ${freqLabel}`.trim();

                  return (
                    <HoverCard
                      key={`prescription-${rx.id}`}
                      position="top"
                      withArrow
                      shadow="md"
                      openDelay={100}
                      closeDelay={200}
                    >
                      <HoverCard.Target>
                        <Box
                          style={{
                            position: 'absolute',
                            left: `${startPosition}%`,
                            top: topPosition,
                            width: `${width}%`,
                            height: `${prescriptionRowHeight}px`,
                            background: `${palette.base}1F`,
                            border: `1px solid ${palette.base}4D`,
                            borderRadius: '999px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: tight ? 5 : 7,
                            padding: tight ? '0 6px' : '0 10px 0 8px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            zIndex: 5,
                            ...(isOngoing
                              ? {
                                  borderRight: 'none',
                                  WebkitMaskImage: ONGOING_FADE_MASK,
                                  maskImage: ONGOING_FADE_MASK,
                                }
                              : {}),
                          }}
                        >
                          <Text
                            style={{
                              fontSize: isMobile ? 11 : 12,
                              fontWeight: 600,
                              color: palette.text,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              minWidth: 0,
                            }}
                          >
                            {rx.drugName}
                          </Text>
                          {!tight && (
                            <Text
                              style={{
                                fontSize: isMobile ? 10 : 10.5,
                                color: '#868e96',
                                whiteSpace: 'nowrap',
                                flex: 'none',
                              }}
                            >
                              {doseLabel}
                            </Text>
                          )}
                        </Box>
                      </HoverCard.Target>
                      <HoverCard.Dropdown>
                        <Stack gap="xs">
                          <Text fw={700}>{`${rx.drugName} - ${doseLabel}`}</Text>
                          <Text size="sm">
                            {toDateString(rx.startDate)} {t('common.to')}{' '}
                            {rx.endDate
                              ? toDateString(rx.endDate)
                              : t('common.present')}
                          </Text>
                        </Stack>
                      </HoverCard.Dropdown>
                    </HoverCard>
                  );
                }),
            )}
          </Box>
          {/* Legend */}
          <Group justify="center" mt="xs" gap="md">
            <Group gap="xs">
              <Box
                style={{
                  width: isMobile ? '12px' : '14px',
                  height: isMobile ? '12px' : '14px',
                  borderRadius: '50%',
                  backgroundColor: TIMELINE_COLORS.lifeEvent,
                }}
              />
              <Text size={isMobile ? 'xs' : 'sm'}>
                {t('table.lifeEvents.lifeEvents')}
              </Text>
            </Group>
            <Group gap="xs">
              <Box
                style={{
                  width: isMobile ? '22px' : '26px',
                  height: isMobile ? '12px' : '14px',
                  background: '#e9ecef',
                  border: '1px solid #ced4da',
                  borderRadius: '999px',
                }}
              />
              <Text size={isMobile ? 'xs' : 'sm'}>Prescription</Text>
            </Group>
          </Group>
        </Box>
      );
    },
  ),
);
